import type { SensorReading } from '@casa/bridge';

// Someone near (docs/specs/settings.md AP-2), from a sensor's readings. A proximity sensor says so
// itself: a change from far to near. A light sensor only sees the light fall as a hand or a person
// comes in front of it. It sees a wide angle, so a hand takes only part of its light: on the owner's
// Galaxy Tab A9+ a hand passed over it took 20 to 30 %, the owner walking up to it 16 %, while the
// room's light varied by up to 6 % by itself (measured 2026-10-28). A lamp switched off looks the
// same and wakes the screen too; a dark room has no light to fall, so there only a tap wakes it.

/** The light must fall below this part of what it was. */
const LIGHT_FALL = 0.88;
/** What it was: the light held at each second from 1 to 5 seconds ago, averaged. */
const LIGHT_PAST_SECONDS = [1, 2, 3, 4, 5];
/** Below this (lux), a fall cannot be told from the sensor's noise. */
const LIGHT_MIN_LUX = 10;
/** After someone was seen, and when the sensor starts (the screen dimming itself), the light settles first. */
const LIGHT_SETTLE_MS = 2_000;

export interface ApproachDetector {
    /** A reading at `at` (ms); calls the detector's `onNear` when it means someone near. */
    reading(reading: SensorReading, at: number) : void;
}

export function approachDetector(onNear: () => void) : ApproachDetector {
    let wasNear: boolean | undefined;
    // Light readings, oldest first. A light sensor reports changes only, so a reading holds until the next.
    let light: { at: number; lux: number }[] = [];
    let settleUntil: number | undefined;

    const heldAt = (at: number) : number | undefined => light.findLast((entry) => entry.at <= at)?.lux;

    return {
        reading(reading, at) {
            if (reading.sensor === 'proximity') {
                // The first reading tells where things are, not that someone came: a cover lying on the sensor.
                if (reading.near && wasNear === false) {
                    onNear();
                }
                wasNear = reading.near;

                return;
            }

            settleUntil ??= at + LIGHT_SETTLE_MS;
            const past = LIGHT_PAST_SECONDS.map((seconds) => heldAt(at - seconds * 1_000)).filter((lux) => lux !== undefined);
            light.push({ at, lux: reading.lux });
            // Kept: the last 5 seconds, and the reading that held before them.
            const oldest = at - LIGHT_PAST_SECONDS.at(-1)! * 1_000;
            const firstKept = Math.max(0, light.findLastIndex((entry) => entry.at <= oldest));
            light = light.slice(firstKept);

            if (at < settleUntil) {
                // What it was while settling does not count: only the light it settled at.
                light = [{ at, lux: reading.lux }];

                return;
            }
            if (past.length === 0) {
                return;
            }

            const before = past.reduce((sum, lux) => sum + lux, 0) / past.length;
            if (before >= LIGHT_MIN_LUX && reading.lux < before * LIGHT_FALL) {
                settleUntil = at + LIGHT_SETTLE_MS;
                // What was before no longer counts: the hand may stay.
                light = [{ at, lux: reading.lux }];
                onNear();
            }
        },
    };
}
